import {css, SerializedStyles} from '@emotion/react';
import {faFilter} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {
  Alert,
  Button,
  Collapse,
  Drawer,
  Group,
  Indicator,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import {isEqual} from 'lodash';
import {ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {useAsyncRetry} from 'react-use';
import {AdvancedQueryPage, Response} from '../../api/generated/index.defs.ts';
import {
  AdvancedPagedRequest,
  AdvancedPagedSearch,
  AnyObject,
} from '../../types';
import {flattenObject} from '../../utils/object-helpers.ts';
import {useDebouncedState} from '../use-debounced-state.ts';
import {useNotification} from '../use-notifications.tsx';
import {useSubscription} from '../use-subscription.ts';
import {DefaultTableView} from './default-table-view.tsx';
import {EditButton, ViewButton} from './row-buttons';
import type {
  AdvancedQueryPagePromise,
  DataComponent,
  FilterDrawerProps,
  PagedDataTableConfig,
  RuntimeConfig,
  SearchComponentProps,
} from './types.ts';
import {usePagination} from './use-pagination.tsx';

export function usePagedDataTable<TDto extends AnyObject>(
  pagedFetchAction: AdvancedQueryPagePromise<TDto>,
  config: PagedDataTableConfig<TDto>,
  runtimeConfig?: RuntimeConfig<TDto>,
  DataComponent?: DataComponent<TDto>,
  dataComponentProps?: AnyObject,
  tableCss?: SerializedStyles,
): ReactNode;
export function usePagedDataTable<TDto extends AnyObject>(
  pagedFetchAction: AdvancedQueryPagePromise<TDto>,
  config: PagedDataTableConfig<TDto>,
  runtimeConfig?: RuntimeConfig<TDto>,
  DataComponent: DataComponent<TDto> = DefaultTableView,
  dataComponentProps?: any,
  tableCss?: SerializedStyles,
): ReactNode {
  const notification = useNotification();
  const pagination = usePagination(
    config.initialPageSize
      ? {
          initialPageSize: config.initialPageSize,
        }
      : undefined,
  );

  const [search, setSearch] = useDebouncedState('', 350);
  const [sort, setSort] = useState(config.defaultSort);
  const [filterOpen, setFilterOpen] = useState(false);

  const cachedResult = useRef<AdvancedQueryPage<TDto> | null | undefined>();
  const additionalParams = useAdditionalParams(runtimeConfig?.additionalParams);
  const onStateChange = runtimeConfig?.onStateChange;

  const fetchData = useAsyncRetry(async () => {
    const searchParams: AdvancedPagedSearch<TDto> = search
      ? {
          searchFieldNames: config.searchFieldNames,
          searchSearchText: search,
        }
      : ({} as AdvancedPagedSearch<TDto>);

    const sortParams: AdvancedPagedRequest<TDto> = sort
      ? {
          sorted: [
            {
              fieldName: sort.column as string,
              descending: sort.direction === 'DESC',
            },
          ],
        }
      : {};

    const params = {
      page: pagination.pageNumber,
      pageSize: pagination.pageSize,
      ...searchParams,
      ...sortParams,
      ...additionalParams,
    };

    if (onStateChange) {
      onStateChange(params);
    }

    try {
      const {data, hasErrors} = await pagedFetchAction(
        params as Parameters<typeof pagedFetchAction>['0'],
      );

      if (hasErrors) {
        notification.error('Failed to fetch data.');
      } else {
        cachedResult.current = data;
        return data;
      }
    } catch (error) {
      notification.error('Failed to fetch data.');
    }
  }, [
    additionalParams,
    config.searchFieldNames,
    notification,
    onStateChange,
    pagedFetchAction,
    pagination.pageNumber,
    pagination.pageSize,
    search,
    sort,
  ]);

  useSubscription(config.refetchEvent || '__NEVER__', () => {
    fetchData.retry();
  });

  const normalizedData = normalizedAdvancedPagedResult<TDto>(
    cachedResult.current,
  );

  const css = useMemo(() => [styles, tableCss], [tableCss]);

  return (
    <div css={css}>
      <SearchRow
        onSearchChange={setSearch}
        runtimeConfig={runtimeConfig}
        setFilterOpen={setFilterOpen}
        filterOpen={filterOpen}
      />
      {fetchData.error && <Alert color='red'>{fetchData.error.message}</Alert>}
      <div className='table-container'>
        <DataComponent
          fetching={fetchData.loading}
          config={config}
          sort={sort}
          onSortChange={setSort}
          records={normalizedData.items}
          dataComponentProps={dataComponentProps}
        />
      </div>

      {pagination.render(
        normalizedData.pageCount,
        normalizedData.totalItemCount,
      )}
    </div>
  );
}

const DefaultSearchComponent = <TDto,>(props: SearchComponentProps<TDto>) => {
  const {onSearchChange, runtimeConfig, setFilterOpen, filterOpen} = props;

  return (
    <>
      {!runtimeConfig?.renderFilterAboveTable ? (
        <Group>
          <TextInput
            onChange={(e) => {
              onSearchChange(e.target.value);
            }}
            placeholder='Search'
          />
          {runtimeConfig?.renderFilter && (
            <FilterDrawer
              filterOpen={filterOpen}
              setFilterOpen={setFilterOpen}
              runtimeConfig={runtimeConfig}
            />
          )}
        </Group>
      ) : (
        <Stack w='100%'>
          <Group w='100%' justify='space-between'>
            <TextInput
              onChange={(e) => {
                onSearchChange(e.target.value);
              }}
              placeholder='Search'
            />
            {runtimeConfig?.renderActions}
          </Group>
          {runtimeConfig?.renderFilter && (
            <FilterDrawer
              filterOpen={filterOpen}
              setFilterOpen={setFilterOpen}
              runtimeConfig={runtimeConfig}
            />
          )}
        </Stack>
      )}
    </>
  );
};

const FilterDrawer = <TDto,>(props: FilterDrawerProps<TDto>) => {
  const {
    filterOpen,
    setFilterOpen,
    runtimeConfig,
    runtimeConfig: {renderFilter},
    runtimeConfig: {renderFilterAboveTable},
  } = props;
  return (
    <>
      {renderFilterAboveTable ? (
        <>
          <FilterButton
            onClick={() => setFilterOpen?.(!filterOpen)}
            runtimeConfig={runtimeConfig}
            filterOpen={filterOpen}
          />
          <Collapse
            in={!!filterOpen}
            w='calc(100vw - 64px)'
            ml='auto'
            mr='auto'
            mb='sm'
          >
            {renderFilter && renderFilter(() => setFilterOpen?.(false))}
          </Collapse>
        </>
      ) : (
        <>
          <FilterButton
            onClick={() => setFilterOpen?.(true)}
            runtimeConfig={runtimeConfig}
          />
          <Drawer.Root
            opened={!!filterOpen}
            onClose={() => setFilterOpen?.(false)}
            position='right'
          >
            <Drawer.Overlay />
            <Drawer.Content>
              <Drawer.Header>
                <Drawer.Title fw={600} fz={'16px'}>
                  Filter
                </Drawer.Title>
                <Drawer.CloseButton />
              </Drawer.Header>
              <Drawer.Body>
                {renderFilter && renderFilter(() => setFilterOpen?.(false))}
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Root>
        </>
      )}
    </>
  );
};

type SearchRowProps<TDto> = SearchComponentProps<TDto>;
const SearchRow = <TDto,>(props: SearchRowProps<TDto>) => {
  const {onSearchChange, runtimeConfig} = props;

  return (
    <Group
      justify='space-between'
      align='center'
      pb={'1em'}
      wrap={'nowrap'}
      gap={0}
    >
      {runtimeConfig?.renderSearch?.(
        onSearchChange,
        DefaultSearchComponent,
      ) || <DefaultSearchComponent {...props} />}
      {!runtimeConfig?.renderFilterAboveTable && runtimeConfig?.renderActions}
    </Group>
  );
};

const AdditionalParams = {};
export function useAdditionalParams(newParams: AnyObject = AdditionalParams) {
  const [state, setState] = useState(newParams);
  useEffect(() => {
    if (!isEqual(state, newParams)) {
      setState(newParams);
    }
  }, [newParams, state]);
  return state;
}

type FilterButton<TDto> = {
  onClick: () => void;
  runtimeConfig?: RuntimeConfig<TDto>;
  filterOpen?: boolean;
};

const FilterButton = <TDto,>(props: FilterButton<TDto>) => {
  const {onClick, runtimeConfig, filterOpen} = props;

  return (
    <Group>
      <Indicator
        color={runtimeConfig?.filterBadgeColor ?? 'red'}
        disabled={!runtimeConfig?.filterBadge}
        label={
          <Text size='11px' lh={11}>
            {runtimeConfig?.filterBadge}
          </Text>
        }
        size={16}
      >
        <Button
          type='button'
          variant='light'
          onClick={onClick}
          leftSection={<FontAwesomeIcon icon={faFilter} />}
        >
          {!runtimeConfig?.renderFilterAboveTable
            ? 'Filter'
            : filterOpen
              ? 'Collapse Filters'
              : 'Expand Filters'}
        </Button>
      </Indicator>
      {runtimeConfig?.filterBadge && runtimeConfig.clearFilterState && (
        <Button
          type='button'
          variant='subtle'
          onClick={runtimeConfig.clearFilterState}
        >
          Clear
        </Button>
      )}
    </Group>
  );
};

export const getActiveFiltersCount = (obj: any) => {
  if (!obj) return 0;
  return Object.values(flattenObject(obj)).filter((x) => Boolean(x)).length;
};

const DEFAULT_RESULT = {
  items: [],
  page: 0,
  pageSize: 0,
  pageCount: 0,
  totalItemCount: 0,
};

function normalizedAdvancedPagedResult<T>(
  value: AdvancedQueryPage<T> | null | undefined,
): AdvancedQueryPage<T> {
  return value || DEFAULT_RESULT;
}

export function PagedDataTableConfig<TDto>(
  pagedFetchAction: (params: any) => Promise<Response<AdvancedQueryPage<TDto>>>,
  config: PagedDataTableConfig<TDto>,
): PagedDataTableConfig<TDto> {
  return config;
}

const styles = css`
  width: 100%;

  .table-container {
    overflow-x: auto;
    padding: 0 0 1em 0;
  }
`;

export {EditButton as renderEditButton, ViewButton as renderViewButton};
